import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { CurrentUser } from "../../api/currentUser";
import { ApiProblem } from "../../api/httpClient";
import { useApiResource } from "../../api/useApiResource";
import { useActorContext } from "../../auth/ActorContext";
import { can } from "../../auth/effectivePermissions";
import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { ForbiddenState } from "../../components/ForbiddenState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { PageHeading } from "../../components/PageHeading";
import { StaleState } from "../../components/StaleState";
import { StatusBadge } from "../../components/StatusBadge";
import {
  exportCemeteries,
  searchCemeteries,
  type CemeteryExportFilters,
  type CemeteryListRow,
  type CemeterySearchFilters,
} from "./cemeteryApi";
import "./cemeteries.css";

const PAGE_SIZE = 20;

type CemeteryTone = "success" | "warning" | "neutral";

import { label } from "../../lib/labels";
import { useCatalog } from "../catalogs/useCatalog";

/**
 * Từ vựng trạng thái nghĩa trang (SCR-032). Mã lạ LUÔN hiển thị nguyên văn, tone
 * neutral — UI không tự suy diễn vòng đời nghĩa trang ngoài hợp đồng.
 *
 * Bảng giữ ở phạm vi module (không export): file này export component nên mọi export
 * khác vi phạm `react-refresh/only-export-components` — lint gate của repo chạy
 * `--max-warnings=0`.
 */
const CEMETERY_STATUS_LABELS: Readonly<
  Record<string, Readonly<{ label: string; tone: CemeteryTone }>>
> = {
  ACTIVE: { label: "Đang quản lý", tone: "success" },
  CLOSED: { label: "Đã đóng", tone: "warning" },
  MERGED: { label: "Đã sáp nhập", tone: "neutral" },
};

function cemeteryStatusLabel(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return CEMETERY_STATUS_LABELS[code]?.label ?? label(code, displayName);
}

function cemeteryStatusTone(code: string): CemeteryTone {
  return CEMETERY_STATUS_LABELS[code]?.tone ?? "neutral";
}

const VI_NUMBER = new Intl.NumberFormat("vi-VN");

/** Số mộ theo định dạng đọc Việt Nam (1.284) — không hiển thị chuỗi thô kiểu 1284. */
function formatCount(value: number): string {
  return VI_NUMBER.format(value);
}

/** Draft thanh lọc SCR-032; trạng thái ĐANG ÁP DỤNG nằm trên URL query (spec §6). */
type CemeteryDraft = Readonly<{ q: string; status: string; adminAreaId: string }>;

const EMPTY_DRAFT: CemeteryDraft = { q: "", status: "", adminAreaId: "" };

const DRAFT_FIELDS: ReadonlyArray<keyof CemeteryDraft> = ["q", "status", "adminAreaId"];

function readDraft(params: URLSearchParams): CemeteryDraft {
  return {
    q: params.get("q") ?? "",
    status: params.get("status") ?? "",
    adminAreaId: params.get("adminAreaId") ?? "",
  };
}

/** `page` trên URL là số trang người dùng đọc được (1-based); API nhận 0-based. */
function readPage(params: URLSearchParams): number {
  const raw = Number(params.get("page") ?? "1");
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
}

function toSearchParams(draft: CemeteryDraft, page: number): URLSearchParams {
  const params = new URLSearchParams();
  for (const field of DRAFT_FIELDS) {
    const value = draft[field];
    if (value !== "") params.set(field, value);
  }
  if (page > 1) params.set("page", String(page));
  return params;
}

/** Predicate dùng chung cho list và export (export không phân trang — rowLimit do server). */
function baseFilters(draft: CemeteryDraft): CemeteryExportFilters {
  const clean = (value: string): string | undefined => (value === "" ? undefined : value);
  const q = clean(draft.q);
  const status = clean(draft.status);
  const adminAreaId = clean(draft.adminAreaId);
  return {
    ...(q !== undefined ? { q } : {}),
    ...(status !== undefined ? { status } : {}),
    ...(adminAreaId !== undefined ? { adminAreaId } : {}),
  };
}

function toApiFilters(draft: CemeteryDraft, page: number): CemeterySearchFilters {
  return { ...baseFilters(draft), page: page - 1, size: PAGE_SIZE };
}

type CemeteryChip = Readonly<{ fields: ReadonlyArray<keyof CemeteryDraft>; label: string }>;

function buildChips(
  draft: CemeteryDraft,
  statusOptions?: readonly { value: string; label: string }[],
): CemeteryChip[] {
  const chips: CemeteryChip[] = [];
  if (draft.q !== "") chips.push({ fields: ["q"], label: `từ khóa: "${draft.q}"` });
  if (draft.status !== "") {
    const opt = statusOptions?.find((o) => o.value === draft.status);
    chips.push({ fields: ["status"], label: `trạng thái: ${opt?.label ?? draft.status}` });
  }
  if (draft.adminAreaId !== "") {
    chips.push({ fields: ["adminAreaId"], label: `địa bàn: ${draft.adminAreaId}` });
  }
  return chips;
}

/** "Nghĩa trang": tên + dòng phụ địa chỉ; thiếu thì bỏ hẳn dòng phụ, không bịa. */
function CemeteryNameCell({ row }: { readonly row: CemeteryListRow }) {
  const address = row.address?.trim() ?? "";
  return (
    <span className="s11-cell-stack">
      <span style={{ fontWeight: 600 }}>{row.name}</span>
      {address !== "" ? <span className="s11-cell-sub">{address}</span> : null}
    </span>
  );
}

/**
 * SCR-032 — Danh sách nghĩa trang (S11.6, mockup UI01): cùng một work surface với
 * SCR-025/SCR-036. Trạng thái đang áp dụng nằm trên URL query; phân trang server-side
 * theo `meta`; `graveCounts` là con số nghiệp vụ thiết yếu, không phải input.
 *
 * Panel nâng cao không được dựng ở màn này: contract `/cemeteries` chỉ hỗ trợ `q`,
 * `status`, `adminAreaId`, `managingOrganizationId`, và hai trường năm xây dựng của
 * mockup không tồn tại trong hợp đồng nên bị bỏ hẳn thay vì hiển thị control rỗng.
 * "Thêm nghĩa trang" / "Xuất danh sách" chỉ render theo `actionCodes` của grant đúng
 * scope — backend vẫn là nơi cưỡng chế quyền.
 */
export function CemeteryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { options: statusOptions } = useCatalog("CEMETERY_STATUS");
  const [draft, setDraft] = useState<CemeteryDraft>(() => readDraft(searchParams));
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<
    Readonly<{ message: string; requestId?: string | undefined }> | null
  >(null);

  const queryKey = searchParams.toString();
  const applied = readDraft(new URLSearchParams(queryKey));
  const page = readPage(new URLSearchParams(queryKey));

  const actorState = useActorContext();
  const actor: CurrentUser | null =
    actorState.status === "ready"
    || actorState.status === "refreshing"
    || actorState.status === "stale"
      ? actorState.actor
      : null;
  const canCreate = actor !== null && can(actor, "DATA_WRITE", { type: "CEMETERY" });
  const canExport = actor !== null && can(actor, "EXPORT_EXECUTE", { type: "CEMETERY" });

  const { state, refetch } = useApiResource(
    (signal) => searchCemeteries(toApiFilters(applied, page), signal),
    [queryKey],
  );

  const setData = state.status === "ready" || state.status === "refreshing" || state.status === "stale"
    ? state.data
    : null;
  const rows = setData?.rows ?? [];
  const meta = setData?.meta;

  const setField = (field: keyof CemeteryDraft, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const onApply = () => {
    setSearchParams(toSearchParams(draft, 1));
  };

  const onClearAll = () => {
    setDraft(EMPTY_DRAFT);
    setSearchParams(toSearchParams(EMPTY_DRAFT, 1));
  };

  const onRemoveChip = (chip: CemeteryChip) => {
    const next: Record<string, string> = { ...draft };
    for (const field of chip.fields) next[field] = "";
    const cleared = next as CemeteryDraft;
    setDraft(cleared);
    setSearchParams(toSearchParams(cleared, 1));
  };

  const onExport = async () => {
    setExporting(true);
    setExportProblem(null);
    try {
      const blob = await exportCemeteries(baseFilters(applied));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "danh-sach-nghia-trang.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const forbidden = error instanceof ApiProblem
        && error.problem.code === "COMMON_FORBIDDEN_SCOPE";
      const requestId = error instanceof ApiProblem ? error.problem.requestId : undefined;
      setExportProblem({
        message: forbidden
          ? "Bạn không có quyền xuất danh sách ở phạm vi hiện tại."
          : "Không thể tạo tệp xuất. Vui lòng thử lại.",
        ...(requestId !== undefined ? { requestId } : {}),
      });
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<CemeteryListRow> = [
    { title: "Mã nghĩa trang", dataIndex: "cemeteryCode", key: "cemeteryCode" },
    {
      title: "Nghĩa trang",
      dataIndex: "name",
      key: "name",
      render: (_value, row) => (
        <Link to={`/cemeteries/${row.cemeteryId}`} aria-label={`Xem nghĩa trang ${row.cemeteryCode}`}>
          <CemeteryNameCell row={row} />
        </Link>
      ),
    },
    {
      title: "Địa bàn",
      dataIndex: "administrativeAreaName",
      key: "administrativeAreaName",
      render: (value: string | null | undefined) =>
        value !== null && value !== undefined && value.trim() !== "" ? value : "—",
    },
    {
      title: "Đơn vị quản lý",
      dataIndex: "managingOrganizationName",
      key: "managingOrganizationName",
      render: (value: string | null | undefined) =>
        value !== null && value !== undefined && value.trim() !== "" ? value : "—",
    },
    {
      title: "Mộ",
      dataIndex: "graveCounts",
      key: "graveCounts",
      render: (value: number) => formatCount(value),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (value: string, row: CemeteryListRow) => (
        <StatusBadge tone={cemeteryStatusTone(value)} label={cemeteryStatusLabel(value, (row as Record<string, unknown>).statusDisplayName)} />
      ),
    },
  ];

  const chips = buildChips(applied, statusOptions);

  return (
    <div className="s11-list-frame">
      <PageHeading
        title="Danh sách nghĩa trang"
        description="Tra cứu nghĩa trang theo mã, tên, địa bàn và trạng thái quản lý trong phạm vi dữ liệu được cấp."
        action={
          canCreate || canExport ? (
            <Space>
              {canCreate ? (
                <Tooltip title="Biểu mẫu tạo nghĩa trang được hoàn thiện ở hạng mục tiếp theo">
                  <Button type="primary" aria-disabled="true">
                    Thêm nghĩa trang
                  </Button>
                </Tooltip>
              ) : null}
              {canExport ? (
                <Button loading={exporting} onClick={() => { void onExport(); }}>
                  Xuất danh sách
                </Button>
              ) : null}
            </Space>
          ) : undefined
        }
      />

      {meta ? (
        <Typography.Text type="secondary" className="s11-list-stats" role="status">
          {`${meta.totalElements} nghĩa trang`}
        </Typography.Text>
      ) : null}

      <section className="card s11-filters" role="search" aria-label="Bộ lọc danh sách nghĩa trang">
        <div className="s11-filter-toolbar">
          <div className="s11-filter-toolbar-search">
            <Input
              id="s11-cemetery-q"
              type="search"
              placeholder="Mã hoặc tên nghĩa trang..."
              aria-label="Tìm nghĩa trang"
              prefix={<SearchOutlined />}
              value={draft.q}
              onChange={(event) => setField("q", event.target.value)}
              onPressEnter={onApply}
            />
          </div>
          <div className="s11-filter-toolbar-slot">
            <Select
              id="s11-cemetery-status"
              placeholder="Tất cả trạng thái"
              aria-label="Trạng thái"
              popupMatchSelectWidth={false}
              allowClear
              value={draft.status === "" ? undefined : draft.status}
              onChange={(value?: string) => setField("status", value ?? "")}
              options={[...statusOptions]}
            />
          </div>
          <div className="s11-filter-toolbar-slot">
            <Input
              id="s11-cemetery-area"
              placeholder="Mã định danh địa bàn"
              aria-label="Địa bàn"
              value={draft.adminAreaId}
              onChange={(event) => setField("adminAreaId", event.target.value)}
            />
          </div>
          <Space className="s11-filter-toolbar-actions">
            <Button icon={<ReloadOutlined />} aria-label="Làm mới" onClick={refetch} />
            <Button type="primary" onClick={onApply}>
              Tìm nghĩa trang
            </Button>
          </Space>
        </div>

        {chips.length > 0 ? (
          <div className="s11-filter-chips" aria-label="Bộ lọc đang áp dụng">
            <Typography.Text type="secondary">Đã lọc:</Typography.Text>
            {chips.map((chip) => (
              <Tag
                key={chip.label}
                closable
                onClose={(event) => {
                  event.preventDefault();
                  onRemoveChip(chip);
                }}
              >
                {chip.label}
              </Tag>
            ))}
            <Button type="link" size="small" onClick={onClearAll}>
              Xóa hết
            </Button>
          </div>
        ) : null}
      </section>

      {exportProblem ? (
        <Alert
          type={exportProblem.message.startsWith("Bạn không có quyền") ? "warning" : "error"}
          showIcon
          role="alert"
          message={exportProblem.message}
          description={
            exportProblem.requestId ? `Mã hỗ trợ: ${exportProblem.requestId}` : undefined
          }
          style={{ margin: "var(--ncc-spacing-sm, 8px) 1.25rem 0" }}
        />
      ) : null}

      {state.status === "loading" ? (
        <div role="status" aria-label="Đang tải danh sách nghĩa trang" className="s11-list-zone">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}
      {state.status === "forbidden" ? (
        <div className="s11-list-zone">
          <ForbiddenState description="Bạn chưa được cấp quyền đọc danh sách nghĩa trang trong phạm vi này." />
        </div>
      ) : null}
      {state.status === "error" ? (
        <div className="s11-list-zone">
          <ErrorState
            title="Không thể tải danh sách nghĩa trang"
            description="Kiểm tra bộ lọc hoặc thử lại sau ít phút."
            {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
            onRetry={refetch}
          />
        </div>
      ) : null}
      {state.status === "stale" ? (
        <div className="s11-list-zone">
          <StaleState onRefresh={refetch} />
        </div>
      ) : null}

      {setData ? (
        rows.length === 0 ? (
          <div className="s11-list-zone">
            <EmptyState
              title="Chưa tìm thấy nghĩa trang phù hợp"
              description="Thử bỏ bớt bộ lọc, đổi từ khóa hoặc kiểm tra lại mã địa bàn."
              action={<Button onClick={onClearAll}>Xóa hết bộ lọc</Button>}
            />
          </div>
        ) : (
          <div className="s11-table-card">
            <Table
              rowKey="cemeteryId"
              columns={columns}
              dataSource={rows}
              size="small"
              loading={state.status === "refreshing"}
              sticky
              onRow={(row) => ({
                className: "s11-row-clickable",
                onClick: (event) => {
                  if ((event.target as HTMLElement).closest("a")) return;
                  if ((window.getSelection()?.toString() ?? "") !== "") return;
                  void navigate(`/cemeteries/${row.cemeteryId}`);
                },
              })}
              pagination={{
                current: meta?.page !== undefined ? meta.page + 1 : page,
                pageSize: meta?.size ?? PAGE_SIZE,
                total: meta?.totalElements ?? 0,
                showSizeChanger: false,
                showTotal: (total, range) => `Hiển thị ${range[0]}–${range[1]} / ${total}`,
                onChange: (nextPage) => {
                  setSearchParams(toSearchParams(applied, nextPage));
                },
              }}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
